import { DestroyRef, ChangeDetectionStrategy, Component, Input, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BazisAutocompleteComponent } from '@bazis/form/components/base/autocomplete/autocomplete.component';
import { MassSelectedService } from '@pages/shared/services/mass-selected.service';
import { BazisSrvService } from '@bazis/shared/services/srv.service';
import { SHARE_REPLAY_SETTINGS } from '@app/configuration.service';
import { of, shareReplay, startWith, switchMap } from 'rxjs';

@Component({
    selector: 'app-autocomplete-multiple',
    templateUrl: './autocomplete-multiple.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [MassSelectedService],
    standalone: false,
})
export class AppAutocompleteMultipleComponent extends BazisAutocompleteComponent {
    private readonly destroyRef = inject(DestroyRef);

    readonly massSrv = inject(MassSelectedService);

    private readonly srvService = inject(BazisSrvService);

    @Input() placeholderKeyValue: string = '';

    @Input() displayCount: number = 6;

    multiple = true;

    private isSyncingFromMassSelection = false;

    override extendOnInit() {
        super.extendOnInit();

        this.massSrv.externalAllSource$ = this.searchValue$.pipe(
            startWith(''),
            switchMap((search) => {
                if (!this.searchSettings?.entityType) {
                    return of([]);
                }

                const params = this.searchSettings.paramsGenerator
                    ? this.searchSettings.paramsGenerator(search)
                    : this.searchSettings.params;

                return this.srvService.allIdsRequest$(this.searchSettings.entityType, {
                    params: params || {},
                    search: this.searchSettings.paramsGenerator ? '' : search || '',
                });
            }),
            shareReplay(SHARE_REPLAY_SETTINGS),
        );

        this.massSrv.allSelected$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((ids: string[]) => {
                if (this.hasSameIds(this.values._, ids)) {
                    return;
                }

                this.isSyncingFromMassSelection = true;
                this.setValueFromMassSelection(ids);
                this.isSyncingFromMassSelection = false;
            });
    }

    override writeValue(value): void {
        super.writeValue(value);
        this.syncMassSelection(this.values._);
    }

    override toggleValue(id: string = '') {
        const shouldResetSelectedAll = this.shouldResetSelectedAll(id);

        super.toggleValue(id);

        if (shouldResetSelectedAll) {
            this.massSrv.setSelectedAllValue([]);
        }

        this.syncMassSelection(this.values._);
    }

    override onClear() {
        this.massSrv.resetAllSelection();
        this.ngControl.control.setValue(this.emptyValue || []);
        super.writeValue(this.ngControl.control.value);
        this.markAsTouched();
    }

    toggleValues(e) {
        e.stopPropagation();
        let countForTrash = this.values._.length - this.displayCount;

        for (let i = 0; i < countForTrash; i++) {
            this.toggleValue(this.values._[this.values._.length - 1]);
        }
    }

    private syncMassSelection(ids: string[]) {
        if (this.isSyncingFromMassSelection) {
            return;
        }

        this.massSrv.selectIds(ids || []);
    }

    private setValueFromMassSelection(ids: string[]) {
        const nextIds = [...(ids || [])];

        this.values.set(nextIds);
        this.valuesMap.clear();
        nextIds.forEach((id) => {
            this.valuesMap.set(id, true);
        });
        this.valuesMap = new Map(this.valuesMap);

        this.onChange([...nextIds]);
        this.markAsTouched();
        this.cdr.markForCheck();
    }

    private hasSameIds(currentIds: string[], nextIds: string[]) {
        const currentSet = new Set(currentIds || []);
        const nextSet = new Set(nextIds || []);

        if (currentSet.size !== nextSet.size) {
            return false;
        }

        return Array.from(currentSet).every((id) => nextSet.has(id));
    }

    private shouldResetSelectedAll(id: string) {
        return !!id && !!this.massSrv.selectedAll.value?.length && this.valuesMap.has(id);
    }
}
